import { z } from 'zod';
import { ApplicantStage } from '../applicant-stage';
import { ReferralStatus } from '../enums';
import { REFERRAL_STATUS_REASON_MAX } from '../referral-status';
import { ReferralPayload } from './my-referrals';

export const REVIEWABLE_REFERRAL_STATUSES = ['submitted', 'needs_clarification', 'under_review', 'reviewed', 'archived'] as const;
export const ReviewableReferralStatus = z.enum(REVIEWABLE_REFERRAL_STATUSES);
export type ReviewableReferralStatus = z.infer<typeof ReviewableReferralStatus>;

export const REFERRAL_DUPLICATE_FILTERS = ['open', 'merged', 'dismissed', 'none'] as const;
export const ReferralDuplicateFilter = z.enum(REFERRAL_DUPLICATE_FILTERS);
export type ReferralDuplicateFilter = z.infer<typeof ReferralDuplicateFilter>;

export const REFERRAL_DUPLICATE_FILTER_LABELS: Record<ReferralDuplicateFilter, string> = {
  open: 'Possible duplicate to review',
  merged: 'Merged duplicate',
  dismissed: 'Dismissed duplicate',
  none: 'No duplicate flagged',
};

const blank = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
const optional = <T extends z.ZodType>(schema: T) => z.preprocess(blank, schema.optional());

export const ReferralListFilters = z.object({
  cycleId: optional(z.string().uuid()),
  trusteeUserId: optional(z.string().uuid()),
  householdId: optional(z.string().uuid()),
  status: optional(ReviewableReferralStatus),
  grade: optional(z.string().trim().max(40)),
  school: optional(z.string().trim().max(160)),
  duplicateStatus: optional(ReferralDuplicateFilter),
  stage: optional(ApplicantStage),
  q: optional(z.string().trim().max(200)),
  submittedFrom: optional(z.iso.date()),
  submittedTo: optional(z.iso.date()),
});
export type ReferralListFilters = z.infer<typeof ReferralListFilters>;

export const submittedRangeOrdered = (q: Pick<ReferralListFilters, 'submittedFrom' | 'submittedTo'>) =>
  !q.submittedFrom || !q.submittedTo || q.submittedFrom <= q.submittedTo;
export const SUBMITTED_RANGE_ISSUE = { path: ['submittedTo'], message: 'The end date must be on or after the start date' };

export const ReferralListQuery = ReferralListFilters.extend({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
}).refine(submittedRangeOrdered, SUBMITTED_RANGE_ISSUE);
export type ReferralListQuery = z.infer<typeof ReferralListQuery>;

export const ReferralTrusteeRef = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  email: z.string(),
});
export type ReferralTrusteeRef = z.infer<typeof ReferralTrusteeRef>;

const ReviewCycleRef = z.object({ id: z.string().uuid(), code: z.string(), label: z.string() });

export const ReferralListChild = z.object({
  name: z.string(),
  grade: z.string(),
  currentSchool: z.string(),
});
export type ReferralListChild = z.infer<typeof ReferralListChild>;

export const ReferralListItem = z.object({
  id: z.string().uuid(),
  referenceNumber: z.string(),
  status: ReferralStatus,
  allowedTransitions: z.array(ReferralStatus),
  cycle: ReviewCycleRef.nullable(),
  trustee: ReferralTrusteeRef,
  familyLastName: z.string(),
  children: z.array(ReferralListChild),
  householdId: z.string().uuid().nullable(),
  countsTowardAllotment: z.boolean(),
  openDuplicates: z.number().int(),
  submittedAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type ReferralListItem = z.infer<typeof ReferralListItem>;

export const ReferralListResponse = z.object({
  items: z.array(ReferralListItem),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type ReferralListResponse = z.infer<typeof ReferralListResponse>;

export const ReferralFilterOptions = z.object({
  grades: z.array(z.string()),
  trustees: z.array(ReferralTrusteeRef),
});
export type ReferralFilterOptions = z.infer<typeof ReferralFilterOptions>;

export const ReferralStatusHistoryEntry = z.object({
  id: z.string().uuid(),
  fromStatus: ReferralStatus.nullable(),
  toStatus: ReferralStatus,
  reason: z.string().nullable(),
  changedByName: z.string().nullable(),
  at: z.string(),
});
export type ReferralStatusHistoryEntry = z.infer<typeof ReferralStatusHistoryEntry>;

export const ReferralReviewDetail = ReferralListItem.omit({ children: true, openDuplicates: true }).extend({
  payload: ReferralPayload,
  clarificationRequest: z.string().nullable(),
  clarificationRequestedAt: z.string().nullable(),
  history: z.array(ReferralStatusHistoryEntry),
});
export type ReferralReviewDetail = z.infer<typeof ReferralReviewDetail>;

export const ChangeReferralStatusInput = z
  .object({
    fromStatus: ReferralStatus,
    status: ReviewableReferralStatus,
    reason: z.string().trim().max(REFERRAL_STATUS_REASON_MAX, `At most ${REFERRAL_STATUS_REASON_MAX} characters`).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'needs_clarification' && !v.reason)
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Explain what the trustee needs to clarify.' });
  });
export type ChangeReferralStatusInput = z.infer<typeof ChangeReferralStatusInput>;
